window.SUBSCRIPTION_PURCHASE_MODE_EVENT = 'subscription:purchase-mode';

if (!customElements.get('subscription-widget')) {
  const SCALABLE_UNIT = /^(?:por|serving|mon|mois|maand|m[åa]ned|mes|mjesec|lun|ay)/i;
  const QUANTIFIED_UNIT = /(\d+)(\s*)(\p{L}+)/gu;

  customElements.define(
    'subscription-widget',
    class SubscriptionWidget extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.formId = this.dataset.formId;
        this.ctaSubscribe = this.dataset.ctaSubscribe;
        this.ctaOnetime = this.dataset.ctaOnetime;
        this.baseVariantId = this.dataset.variantId;

        this.init();

        this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          this.onVariantChange.bind(this)
        );

        this.onQtyChange = this.onQuantityChange.bind(this);
        document.addEventListener('change', this.onQtyChange);
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        document.removeEventListener('change', this.onQtyChange);
      }

      init() {
        this.toggleButtons = Array.from(this.querySelectorAll('.subscription-widget__toggle-btn'));
        this.panels = Array.from(this.querySelectorAll('.subscription-widget__panel'));

        this.bindEvents();
        this.applyInitialState();
      }

      onVariantChange(event) {
        const html = event?.data?.html;
        if (!html) return;
        const fresh = html.getElementById(`Subscription-${this.sectionId}`);
        if (!fresh) return; // event belongs to a different product-info (e.g. quick view)

        this.dataset.hasPlans = fresh.dataset.hasPlans;
        this.dataset.variantId = fresh.dataset.variantId;
        this.baseVariantId = fresh.dataset.variantId;
        this.innerHTML = fresh.innerHTML;
        this.init();
      }

      get productId() {
        return this.dataset.productId;
      }

      get productForms() {
        const forms = Array.from(document.querySelectorAll(`form[data-product-id="${this.productId}"]`));
        if (forms.length) return forms;
        const fallback = this.formId ? document.getElementById(this.formId) : this.closest('form');
        return fallback ? [fallback] : [];
      }

      get submitButton() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      quantityInputIn(form) {
        return form.querySelector('quantity-input input, input[name="quantity"]');
      }

      tierRadiosIn(mode) {
        return Array.from(this.querySelectorAll(`[data-panel="${mode}"] .subscription-widget__tier-radio`));
      }

      checkedTierIn(mode) {
        const radios = this.tierRadiosIn(mode);
        return radios.find((r) => r.checked) || radios[0] || null;
      }

      bindEvents() {
        this.toggleButtons.forEach((btn) => {
          btn.addEventListener('click', () => this.setMode(btn.dataset.mode));
        });
        this.querySelectorAll('.subscription-widget__tier-radio').forEach((radio) => {
          radio.addEventListener('change', () => this.selectTier(radio));
        });
        this.querySelector('[data-abo-nudge]')?.addEventListener('click', () => this.setMode('subscription'));
      }

      applyInitialState() {
        const hasSubscription = this.dataset.hasPlans === 'true' && this.tierRadiosIn('subscription').length > 0;
        if (!hasSubscription) {
          this.setMode('onetime', 'init');
          return;
        }
        const activeToggle = this.toggleButtons.find((b) => b.classList.contains('is-active'));
        this.setMode(activeToggle ? activeToggle.dataset.mode : 'subscription', 'init');
      }

      setMode(mode, source = 'user') {
        this.currentMode = mode;
        this.toggleButtons.forEach((btn) => {
          const active = btn.dataset.mode === mode;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        this.panels.forEach((panel) => {
          panel.hidden = panel.dataset.panel !== mode;
        });

        const tier = this.checkedTierIn(mode);
        if (tier) {
          tier.checked = true;
          this.selectTier(tier);
        } else {
          this.setSellingPlan('');
          this.setFormVariant(this.baseVariantId);
          this.setCtaText(this.ctaOnetime);
          this.setQuantityVisible(true);
        }

        document.dispatchEvent(
          new CustomEvent(window.SUBSCRIPTION_PURCHASE_MODE_EVENT, {
            detail: { productId: this.productId, mode, source },
          })
        );
      }

      selectTier(radio) {
        const planId = radio.dataset.sellingPlan || '';
        this.setSellingPlan(planId);
        this.setCtaText(planId ? this.ctaSubscribe : this.ctaOnetime);
        this.updateMainPrice(parseInt(radio.dataset.totalPrice, 10));

        const qty = parseInt(radio.dataset.quantity, 10);
        if (planId) {
          this.setFormVariant(radio.dataset.variant || this.baseVariantId);
          if (!Number.isNaN(qty)) this.setQuantity(qty);
          this.setQuantityVisible(false);
        } else {
          this.setFormVariant(this.baseVariantId);
          this.setQuantityVisible(true);
          if (!Number.isNaN(qty)) {
            this.setQuantity(qty);
            this.syncOneTimeTiers(qty);
          }
        }
      }

      setFormVariant(id) {
        if (!id) return;
        this.productForms.forEach((form) => {
          const input = form.querySelector('input[name="id"]');
          if (input && input.value !== String(id)) input.value = String(id);
        });
      }

      setQuantity(qty) {
        this.productForms.forEach((form) => {
          let input = this.quantityInputIn(form);
          if (!input) {
            input = form.querySelector('input[name="quantity"][data-subscription-qty]');
            if (!input) {
              input = document.createElement('input');
              input.type = 'hidden';
              input.name = 'quantity';
              input.setAttribute('data-subscription-qty', '');
              form.appendChild(input);
            }
          }
          if (input.value !== String(qty)) {
            input.value = qty;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      setQuantityVisible(visible) {
        this.productForms.forEach((form) => {
          form.querySelectorAll('.product-form__quantity, [id^="QuantitySticky"], quantity-input, .quantity__label').forEach((el) => {
            el.style.display = visible ? '' : 'none';
          });
        });
      }

      onQuantityChange(event) {
        const input = event.target;
        if (!input || input.name !== 'quantity' || this.currentMode !== 'onetime') return;
        if (!this.productForms.includes(input.closest('form'))) return;

        const qty = parseInt(input.value, 10);
        if (Number.isNaN(qty) || qty < 1) return;

        const options = this.tierRadiosIn('onetime')
          .map((r) => ({ r, q: parseInt(r.dataset.quantity, 10) }))
          .filter((o) => !Number.isNaN(o.q))
          .sort((a, b) => a.q - b.q);
        if (!options.length) return;

        const eligible = options.filter((o) => o.q <= qty);
        const target = options.find((o) => o.q === qty) || eligible[eligible.length - 1] || options[0];
        if (target && !target.r.checked) {
          target.r.checked = true;
          this.setSellingPlan('');
        }

        this.syncOneTimeTiers(qty);
      }

      syncOneTimeTiers(qty) {
        const openEnded = this.querySelector('.subscription-widget__tier-radio[data-open-ended]');
        if (openEnded) {
          const oeQty = parseInt(openEnded.dataset.quantity, 10);
          this.renderOneTimeTier(openEnded, openEnded.checked && qty > oeQty ? qty : oeQty);
        }

        const checked = this.checkedTierIn('onetime');
        if (checked) {
          const tierQty = parseInt(checked.dataset.quantity, 10);
          const effectiveQty = checked.hasAttribute('data-open-ended') && qty > tierQty ? qty : tierQty;
          this.updateMainPrice(this.oneTimeTotal(checked, effectiveQty));
        }
      }

      oneTimeTotal(radio, qty) {
        const tierQty = parseInt(radio.dataset.quantity, 10);
        if (radio.hasAttribute('data-open-ended') && qty > tierQty) {
          const unit = parseFloat(radio.dataset.unitPrice);
          const disc = parseFloat(radio.dataset.discount) || 0;
          return Math.round((qty * unit * (100 - disc)) / 100);
        }
        return parseInt(radio.dataset.totalPrice, 10);
      }

      renderOneTimeTier(radio, qty) {
        const row = radio.closest('.subscription-widget__tier');
        if (!row) return;
        const tierQty = parseInt(radio.dataset.quantity, 10);
        const compareUnit = parseFloat(radio.dataset.compareUnitPrice || radio.dataset.unitPrice);
        const total = this.oneTimeTotal(radio, qty);
        const compare = Number.isFinite(compareUnit) ? Math.round(qty * compareUnit) : total;

        const priceEl = row.querySelector('.subscription-widget__price');
        if (priceEl) priceEl.textContent = this.money(total);
        const compareEl = row.querySelector('.subscription-widget__price-compare');
        if (compareEl) {
          compareEl.textContent = this.money(compare);
          compareEl.style.display = compare - total > 0 ? '' : 'none';
        }
        const labelEl = row.querySelector('[data-tier-label]');
        if (labelEl) {
          const base = labelEl.dataset.label || '';
          labelEl.textContent = qty > tierQty ? this.relabel(base, tierQty, qty) : base;
        }
        const portionsEl = row.querySelector('[data-tier-portions]');
        if (portionsEl) {
          const base = portionsEl.dataset.label || '';
          portionsEl.textContent = qty > tierQty ? this.scaleUnits(base, tierQty, qty) : base;
        }
      }

      relabel(base, fromQty, toQty) {
        const replaced = base.replace(new RegExp(`\\b${fromQty}\\b`), String(toQty));
        return replaced === base ? `${base} ×${toQty}` : replaced;
      }

      scaleUnits(base, fromQty, toQty) {
        if (!fromQty || fromQty === toQty) return base;
        return base.replace(QUANTIFIED_UNIT, (match, count, gap, unit) =>
          SCALABLE_UNIT.test(unit) ? `${Math.round((parseInt(count, 10) / fromQty) * toQty)}${gap}${unit}` : match
        );
      }

      money(cents) {
        return FoxTheme?.Currency ? FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat) : String(cents);
      }

      setSellingPlan(planId) {
        const value = planId || '';
        this.productForms.forEach((form) => {
          const formId = form.getAttribute('id');
          const inputs = [
            ...new Set([
              ...form.querySelectorAll('input[name="selling_plan"]'),
              ...(formId ? document.querySelectorAll(`input[name="selling_plan"][form="${CSS.escape(formId)}"]`) : []),
            ]),
          ];
          let input = inputs.shift();
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'selling_plan';
            form.appendChild(input);
          }
          inputs.forEach((duplicate) => duplicate.remove());
          input.value = value;
        });
      }

      setCtaText(text) {
        if (!text) return;
        const btn = this.submitButton;
        const label = btn?.querySelector('span:not(.visually-hidden)') || btn;
        if (label) label.textContent = text;
      }

      updateMainPrice(totalCents) {
        if (!Number.isFinite(totalCents)) return;
        const priceBlock = document.getElementById(`price-${this.sectionId}`);
        const regular = priceBlock?.querySelector('.f-price-item--regular');
        if (regular && FoxTheme?.Currency) {
          regular.innerHTML = FoxTheme.Currency.formatMoney(totalCents, FoxTheme.settings.moneyFormat);
        }
      }
    }
  );
}

if (!customElements.get('sticky-purchase-cta')) {
  customElements.define(
    'sticky-purchase-cta',
    class StickyPurchaseCta extends HTMLElement {
      connectedCallback() {
        this.planVariants = (this.dataset.planVariants || '').split(',').filter(Boolean);
        this.chooseBtn = this.querySelector('[data-choose-options]');
        this.atcBtn = this.querySelector('[data-atc]');
        this.form = this.closest('form');
        this.variantInput = this.form?.querySelector('[name="id"]');
        this.productId = this.form?.dataset.productId;
        this.mode = this.widget?.currentMode || null;

        this.chooseBtn?.addEventListener('click', () => {
          this.widget?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        this.variantInput?.addEventListener('change', () => this.update());
        this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          (event) => this.update(event?.data?.variant?.id)
        );
        this.onPurchaseModeChange = (event) => {
          if (this.productId && event.detail?.productId !== this.productId) return;
          this.mode = event.detail?.mode || null;
          this.update();
        };
        document.addEventListener(window.SUBSCRIPTION_PURCHASE_MODE_EVENT, this.onPurchaseModeChange);
        this.update();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        document.removeEventListener(window.SUBSCRIPTION_PURCHASE_MODE_EVENT, this.onPurchaseModeChange);
      }

      get widget() {
        return this.productId
          ? document.querySelector(`subscription-widget[data-product-id="${this.productId}"]`)
          : document.querySelector('subscription-widget');
      }

      update(variantId) {
        const id = variantId != null ? String(variantId) : this.variantInput ? String(this.variantInput.value) : '';
        const hasPlans = this.planVariants.includes(id) && this.mode !== 'onetime';
        if (this.chooseBtn) this.chooseBtn.style.display = hasPlans ? '' : 'none';
        if (this.atcBtn) this.atcBtn.style.display = hasPlans ? 'none' : '';
        const dynamic = this.form?.querySelector('.product-form__button-dynamic');
        if (dynamic) dynamic.style.display = hasPlans ? 'none' : '';
      }
    }
  );
}
